/**
 * การจัดวางรูปในเนื้อหาบทความ
 *
 * ไฟล์นี้เป็นแหล่งความจริงเดียวของ "รูปวางยังไงได้บ้าง" ให้ทั้งสามฝั่ง:
 *   - หน้าแก้ไข (TipTap node view + CSS ของ .tiptap)
 *   - ตัวแปลง TipTap JSON → HTML ฝั่ง API
 *   - หน้าบล็อกจริง (CSS ของ .prose)
 * ถ้าแยกกันเขียน สิ่งที่นักเขียนเห็นตอนจัดวางจะไม่ตรงกับที่ผู้อ่านเห็น
 * ซึ่งเป็นปัญหาที่แก้ทีหลังยากมาก
 */
export const FIGURE_ALIGNS = ['center', 'left', 'right', 'wrap-left', 'wrap-right', 'wrap-both'];
export function isFigureAlign(v) {
    return typeof v === 'string' && FIGURE_ALIGNS.includes(v);
}
export function figureAlign(v) {
    return isFigureAlign(v) ? v : 'center';
}
/**
 * ความกว้างเก็บเป็น "เปอร์เซ็นต์ของคอลัมน์เนื้อหา" ไม่ใช่พิกเซล
 * เพราะความกว้างเนื้อหาของบล็อกปรับได้ในหน้าธีม (600–1600px)
 * ถ้าเก็บเป็น px รูปที่จัดไว้สวยในหน้าแก้ไขจะเพี้ยนทันทีที่เจ้าของบล็อกเปลี่ยนธีม
 * null = เต็มความกว้าง
 */
export function figureWidth(v) {
    // ต้องเช็ก null/'' ก่อน เพราะ Number(null) และ Number('') ได้ 0
    // ซึ่งจะถูก clamp เป็น 10% ทำให้รูปที่ยังไม่เคยตั้งความกว้างหดเหลือนิดเดียว
    if (v === null || v === undefined || v === '')
        return null;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n))
        return null;
    const rounded = Math.round(n);
    if (rounded >= 100)
        return null;
    return Math.min(100, Math.max(10, rounded));
}
/** ความกว้างเริ่มต้นเมื่อสั่งให้รูป "ลอยข้างข้อความ" — เต็มความกว้างแล้วข้อความไม่มีที่ไหลไปอยู่ */
export const FIGURE_WRAP_DEFAULT_WIDTH = 45;
/**
 * โหมด wrap-both = รูปอยู่กลาง ข้อความแบ่งเป็นสองคอลัมน์ขนาบซ้าย-ขวา
 *
 * CSS ไม่มี float:center และไม่มีทางให้ข้อความไหลรอบทั้งสองข้างของรูปได้
 * (สเปค CSS Exclusions มี wrap-flow:both ไว้เพื่อการนี้ แต่ไม่มีเบราว์เซอร์ไหนทำจริง)
 * จึงใช้วิธีเดียวกับหน้านิตยสาร: ข้อความเป็นสองคอลัมน์ รูปนั่งอยู่ในร่องกลาง
 * และเพราะรูปต้องอยู่ในกล่องเดียวกับข้อความ node นี้จึงมีลูกเป็นย่อหน้าได้
 */
export const FIGURE_SPLIT_ALIGN = 'wrap-both';
/** ระยะห่างระหว่างรูปกับคอลัมน์ข้อความสองข้าง — ใช้ตรงกันทั้ง grid และ column-gap ไม่งั้นขอบจะไม่ตรง */
export const FIGURE_SPLIT_GUTTER = '1.6em';
export function isSplitAlign(align) {
    return align === FIGURE_SPLIT_ALIGN;
}
/** ปุ่มขนาดสำเร็จรูปในแถบเครื่องมือของรูป */
export const FIGURE_WIDTH_PRESETS = [25, 50, 75, 100];
export function figureClass(align) {
    return `ba-fig ba-fig--${align}`;
}
/**
 * CSS ของรูปในเนื้อหา — ใช้ร่วมกันระหว่างหน้าแก้ไขกับหน้าบล็อกจริง
 * @param scope  selector ของกล่องเนื้อหา เช่น '.tiptap' หรือ '.prose'
 * @param radius ค่า border-radius ที่แต่ละฝั่งใช้ (admin เป็น px, บล็อกเป็น token ของธีม)
 */
export function figureCss(scope, radius = '10px') {
    const s = scope;
    const g = FIGURE_SPLIT_GUTTER;
    return `
${s} .ba-fig{margin:1.8em 0;max-width:100%}
${s} .ba-fig img{display:block;width:100%;height:auto;border-radius:${radius}}
${s} .ba-fig figcaption{margin-top:8px;font-size:.85rem;line-height:1.6;text-align:center}
${s} .ba-fig--center{margin-left:auto;margin-right:auto}
${s} .ba-fig--left{margin-left:0;margin-right:auto}
${s} .ba-fig--right{margin-left:auto;margin-right:0}
${s} .ba-fig--wrap-left{float:left;margin:.35em ${g} 1.1em 0}
${s} .ba-fig--wrap-right{float:right;margin:.35em 0 1.1em ${g}}
/* กันรูปลอยหลุดออกนอกบทความไปทับส่วนอื่นของหน้า */
${s}::after{content:"";display:block;clear:both}

/* ---- รูปกลาง ข้อความสองข้าง (แบบนิตยสาร) ----
   รูปกับข้อความอยู่แถวเดียวกันของ grid ความสูงของแถวจึงเท่ากับอันที่สูงกว่า
   ถ้าข้อความสั้นกว่ารูป แถวก็ยังสูงเท่ารูป เนื้อหาถัดไปจึงไม่ทับรูป
   ส่วนข้อความ span ทั้งสามคอลัมน์แล้วแบ่งตัวเองเป็นสองคอลัมน์
   โดยตั้งร่องกลางให้กว้างเท่ารูป + ระยะห่างสองข้าง ขอบคอลัมน์จึงตรงกับขอบรูปพอดี */
${s} .ba-fig--wrap-both{
  display:grid;grid-template-columns:1fr var(--fig-w,45%) 1fr;
  column-gap:${g};align-items:start;width:auto;float:none}
${s} .ba-fig--wrap-both>.ba-fig__media{grid-area:1/2/2/3;min-width:0}
${s} .ba-fig--wrap-both>.ba-fig__flow{
  grid-area:1/1/2/4;min-width:0;
  columns:2;column-gap:calc(var(--fig-w,45%) + ${g} * 2)}
${s} .ba-fig__flow>*:first-child{margin-top:0}
${s} .ba-fig__flow>*:last-child{margin-bottom:0}

/* บนมือถือคอลัมน์แคบเกินกว่าจะให้ข้อความไหลข้างรูปได้ — คืนเป็นเต็มความกว้างทุกกรณี */
@media (max-width:640px){
${s} .ba-fig{float:none!important;width:auto!important;margin-left:0;margin-right:0}
${s} .ba-fig--wrap-both{display:block}
${s} .ba-fig--wrap-both>.ba-fig__flow{columns:auto;margin-top:1.1em}
}`;
}
/* ------------------------------------------------------------------ *
 * ซ่อมเอกสารที่มีรูปซ้อนอยู่ในรูป
 * ------------------------------------------------------------------ */
/** ชนิดลูกที่ช่องข้อความของโหมดนิตยสารรับได้ — ต้องตรงกับ content ของ node ในหน้าแก้ไข */
const FIGURE_FLOW_TYPES = new Set(['paragraph']);
/** คืน [node ที่สะอาดแล้ว, ...node ที่ถูกยกออกมาเป็นพี่น้องถัดไป] */
function repairNode(node) {
    if (!node || typeof node !== 'object')
        return [];
    const kids = Array.isArray(node.content) ? node.content : null;
    if (!kids)
        return [node];
    if (node.type === 'image') {
        const keep = [];
        const lifted = [];
        for (const child of kids) {
            if (FIGURE_FLOW_TYPES.has(String(child?.type)))
                keep.push(...repairNode(child));
            else
                lifted.push(...repairNode(child)); // เช่นรูปที่ถูกลากเข้าไปซ้อน — ยกออกมาไว้ถัดจากรูปแม่
        }
        const self = { ...node, content: keep };
        if (keep.length === 0)
            delete self.content;
        return [self, ...lifted];
    }
    const out = [];
    for (const child of kids)
        out.push(...repairNode(child));
    return [{ ...node, content: out }];
}
/**
 * ดึงรูปที่ซ้อนอยู่ในช่องข้อความของรูปอื่นออกมาเป็นพี่น้อง
 *
 * schema ปัจจุบันกันไม่ให้ซ้อนใหม่ได้แล้ว แต่เอกสารที่เคยถูกเขียนไว้ก่อนหน้านั้นยังซ้อนอยู่
 * และถ้าปล่อยไว้จะกลายเป็นเนื้อหาที่แก้ไม่ได้ — เรียกทั้งตอนเปิดในหน้าแก้ไขและตอน render
 * ฝั่ง server ผู้อ่านจึงเห็นหน้าที่ถูกต้องทันที ไม่ต้องรอผู้เขียนมาเปิดบันทึกใหม่
 */
export function repairNestedFigures(doc) {
    const root = doc;
    if (!root || typeof root !== 'object' || !Array.isArray(root.content))
        return doc;
    const content = [];
    for (const child of root.content)
        content.push(...repairNode(child));
    return { ...root, content };
}
/** นับรูปที่ซ้อนอยู่ผิดที่ — ใช้ตัดสินใจว่าต้องซ่อมไหม จะได้ไม่สร้าง object ใหม่ทิ้ง ๆ ขว้าง ๆ */
export function countNestedFigures(doc, insideFigure = false) {
    const node = doc;
    if (!node || typeof node !== 'object')
        return 0;
    const isImage = node.type === 'image';
    const own = isImage && insideFigure ? 1 : 0;
    const kids = Array.isArray(node.content) ? node.content : [];
    return own + kids.reduce((sum, k) => sum + countNestedFigures(k, insideFigure || isImage), 0);
}
//# sourceMappingURL=figure.js.map